import { useEffect, useRef, useState } from 'react'
import { PROPERTY_DRAG_RAILS, useHorizontalDrag } from './useHorizontalDrag'
import './property-theme.scss'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { doc, onSnapshot } from '@/lib/property-data'
import {
  ArrowUp,
  Building2,
  Calculator,
  ChevronDown,
  ChevronRight,
  CirclePlus,
  Home as HomeIcon,
  Landmark,
  LogIn,
  LogOut,
  Menu,
  Megaphone,
  UserRound,
  WandSparkles,
  X,
} from 'lucide-react'

import { PropertyAiWidget } from '@/components/property/PropertyAiToolsPanel'
import {
  PROPERTY_LOCATION_MENU_ITEMS,
  PROPERTY_MEGA_MENU_CONFIGS,
  PROPERTY_TYPE_MENU_ITEMS,
  POPULAR_PROPERTY_LOCATION_ITEMS,
  buildPropertyHref,
  propertySlug,
  type PropertyMegaMenuKind,
} from '@/data/property-mega-menu'
import { PROPERTY_LISTING_BILLING } from '@/data/property-listing-config'
import { showAppToast } from '@/components/app/AppToast'
import { firebaseAuth } from '@/lib/firebase'
import { propertyDb } from '@/lib/property-data'
import { propertyFetch } from '@/lib/property-api'

import type { AppLocale } from '@/lib/i18n'
import { handlePropertyRegisterModalNavigation, navigateTo } from '@/lib/navigation'
import '@/pages/home/home-landing-page.scss'

type PropertySiteHeaderProps = {
  locale: AppLocale
  onLocaleChange: (nextLocale: AppLocale) => void
}

const KPR_MENU_ITEMS = [
  {
    title: 'Simulasi KPR',
    description: 'Hitung cicilan rumah dengan mudah',
    href: '/kpr/simulasi-kpr',
    icon: Calculator,
  },
  {
    title: 'Simulasi KPR Syariah',
    description: 'Hitung pembiayaan rumah dengan prinsip islami',
    href: '/kpr/syariah',
    icon: Landmark,
  },
]

const FOOTER_FEATURED_PROPERTY_GROUPS = [
  {
    title: 'Rumah & Apartemen',
    description: 'Pencarian hunian populer untuk keluarga, pekerja, dan investor.',
    links: [
      { label: 'Rumah dijual di Jakarta Selatan', href: '/jual/rumah/jakarta-selatan' },
      { label: 'Apartemen dijual di BSD', href: '/jual/apartemen/bsd' },
      { label: 'Rumah siap huni', href: '/jual/rumah' },
      { label: 'Rumah disewa', href: '/sewa/rumah' },
    ],
  },
  {
    title: 'Tanah & Komersial',
    description: 'Pilihan aset produktif untuk usaha, gudang, ruko, dan lahan.',
    links: [
      { label: 'Tanah dijual di Bandung', href: '/jual/tanah/bandung' },
      { label: 'Ruko dijual di Surabaya', href: '/jual/ruko/surabaya' },
      { label: 'Properti komersial', href: '/jual/ruang-usaha' },
      { label: 'Gudang dijual dan disewa', href: '/jual/gudang' },
    ],
  },
  {
    title: 'Properti Baru & Tools',
    description: 'Temukan listing terbaru dan cek estimasi cicilan sebelum survei.',
    links: [
      { label: 'Villa dijual di Bali', href: '/jual/villa/bali' },
      { label: 'Properti baru dari developer', href: '/properti-baru' },
      { label: 'Listing properti terbaru', href: '/jual' },
      { label: 'Simulasi KPR rumah', href: '/kpr/simulasi-kpr' },
    ],
  },
]

const FOOTER_LOCATION_LINKS = POPULAR_PROPERTY_LOCATION_ITEMS

const FOOTER_COMPANY_LINKS = [
  { label: 'Tentang Kami', href: '/tentang-kami' },
  { label: 'Partner', href: '/partner' },
  { label: 'Kompetitor', href: '/kompetitor' },
]

const FOOTER_SERVICE_LINKS = [
  { label: 'Iklankan Properti', href: '/pasang-property' },
  { label: 'Login sebagai Agen', href: '/agent' },
  { label: 'KPR', href: '/kpr/simulasi-kpr' },
  { label: 'Properti Baru', href: '/properti-baru' },
  { label: 'Artikel', href: '/artikel' },
]

const FOOTER_LEGAL_LINKS = [
  { label: 'FAQ', href: '/faq' },
  { label: 'Kebijakan Privasi', href: '/privacy-policy' },
  { label: 'Syarat Penggunaan', href: '/terms-of-use' },
  { label: 'Syarat Penggunaan Agen', href: '/syarat-penggunaan-agen' },
]

const FOOTER_SOCIAL_LINKS = [
  {
    label: 'Instagram Dihaw.co',
    href: 'https://www.instagram.com/dihaw.co/',
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/ig.png',
  },
  {
    label: 'TikTok Dihaw.co',
    href: 'https://www.tiktok.com/@dihaw.co',
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/tiktok7.png',
  },
  {
    label: 'Threads Dihaw.co',
    href: 'https://www.threads.com/@dihaw.co',
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/threads.png',
  },
  {
    label: 'LinkedIn Dihaw.co',
    href: 'https://www.linkedin.com/company/143889721',
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/linkedin.png',
  },
  {
    label: 'Facebook Dihaw.co',
    href: 'https://www.facebook.com/profile.php?id=61594835572968',
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/fb.png',
  },
  {
    label: 'X Dihaw.co',
    href: 'https://x.com/dihawco',
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/x.png',
  },
]

const FOOTER_ASK_AI_PROMPT = encodeURIComponent(
  'As a potential customer, explain Dihaw.co (Dihaw.co) in simple terms: what it does, who it is for, how users search property listings for sale, rent, and new projects, compare prices and specifications, use AI-assisted listing discovery, contact property owners or agents via WhatsApp, use KPR and KPR Syariah simulation, and how property owners, in-house marketing teams, developers, and agents can publish and manage listings.',
)

const FOOTER_ASK_AI_LINKS = [
  {
    label: 'ChatGPT',
    href: `https://chatgpt.com/?q=${FOOTER_ASK_AI_PROMPT}`,
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/chatgpt.png',
  },
  {
    label: 'Claude',
    href: `https://claude.ai/new?q=${FOOTER_ASK_AI_PROMPT}`,
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/claude.png',
  },
  {
    label: 'Grok',
    href: `https://grok.com/?q=${FOOTER_ASK_AI_PROMPT}`,
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/grok.webp',
  },
  {
    label: 'Perplexity',
    href: `https://www.perplexity.ai/search/new?q=${FOOTER_ASK_AI_PROMPT}`,
    iconSrc: 'https://cdn.dihaw.ai/cdn-cgi/image/format=webp/public/perplexity.webp',
  },
]

type PropertyHeaderAccountKind = 'guest' | 'user' | 'agent'

export function PropertySiteHeader({ locale: _locale, onLocaleChange: _onLocaleChange }: PropertySiteHeaderProps) {
  useHorizontalDrag(PROPERTY_DRAG_RAILS)
  const [openPropertyMenu, setOpenPropertyMenu] = useState<PropertyMegaMenuKind | null>(null)
  const [isKprMenuOpen, setIsKprMenuOpen] = useState(false)
  const [isAiToolsOpen, setIsAiToolsOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [propertyAccountKind, setPropertyAccountKind] = useState<PropertyHeaderAccountKind>('guest')
  const [selectedTypeSlug, setSelectedTypeSlug] = useState(PROPERTY_TYPE_MENU_ITEMS[0]?.slug ?? '')
  const [selectedProvinceSlug, setSelectedProvinceSlug] = useState(PROPERTY_LOCATION_MENU_ITEMS[0]?.slug ?? '')
  const propertyMenuCloseTimerRef = useRef<number | null>(null)

  const activePropertyMenuConfig =
    PROPERTY_MEGA_MENU_CONFIGS.find((item) => item.id === openPropertyMenu) ?? null
  const selectedType =
    PROPERTY_TYPE_MENU_ITEMS.find((item) => item.slug === selectedTypeSlug) ?? PROPERTY_TYPE_MENU_ITEMS[0]
  const selectedProvince =
    PROPERTY_LOCATION_MENU_ITEMS.find((item) => item.slug === selectedProvinceSlug) ?? PROPERTY_LOCATION_MENU_ITEMS[0]
  const isFreeListingMode = PROPERTY_LISTING_BILLING.mode === 'free'
  const isPropertyUserLoggedIn = propertyAccountKind !== 'guest'
  const isAgentLoggedIn = propertyAccountKind === 'agent'
  const isRegularPropertyUserLoggedIn = propertyAccountKind === 'user'
  const propertyRegisterHref = isRegularPropertyUserLoggedIn ? '/pasang-property/profile' : '/pasang-property'
  const agentRegisterHref = isAgentLoggedIn ? '/agent/profile' : '/agent'
  const profileHref = isAgentLoggedIn ? '/agent/profile' : '/pasang-property/profile'
  const profileLabel = isAgentLoggedIn ? 'Profile Agent' : 'Profile'


  useEffect(() => {
    let profileUnsubscribe: (() => void) | null = null
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (profileUnsubscribe) {
        profileUnsubscribe()
        profileUnsubscribe = null
      }
      if (!user) {
        setPropertyAccountKind('guest')
        setIsUserMenuOpen(false)
        return
      }
      profileUnsubscribe = onSnapshot(
        doc(propertyDb, 'propertyUsers', user.uid),
        (snapshot) => {
          const accountType = String(snapshot.data()?.accountType || '')
          setPropertyAccountKind(accountType === 'agent' ? 'agent' : 'user')
        },
        () => setPropertyAccountKind(window.location.pathname.startsWith('/agent') ? 'agent' : 'user'),
      )
    })

    return () => {
      unsubscribe()
      if (profileUnsubscribe) profileUnsubscribe()
    }
  }, [])

  useEffect(() => {
    let intervalId: number | null = null
    const recordActivity = () => {
      if (!firebaseAuth.currentUser || document.visibilityState !== 'visible') return
      void propertyFetch('/v1/property/me/activity', { method: 'POST', body: {} }).catch(() => undefined)
    }
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (intervalId !== null) window.clearInterval(intervalId)
      intervalId = null
      if (!user) return
      recordActivity()
      intervalId = window.setInterval(recordActivity, 5 * 60 * 1000)
    })
    document.addEventListener('visibilitychange', recordActivity)
    return () => {
      unsubscribe()
      if (intervalId !== null) window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', recordActivity)
    }
  }, [])

  const clearPropertyMenuCloseTimer = () => {
    if (propertyMenuCloseTimerRef.current === null) return
    window.clearTimeout(propertyMenuCloseTimerRef.current)
    propertyMenuCloseTimerRef.current = null
  }

  const resetMegaMenuSelection = () => {
    setSelectedTypeSlug(PROPERTY_TYPE_MENU_ITEMS[0]?.slug ?? '')
    setSelectedProvinceSlug(PROPERTY_LOCATION_MENU_ITEMS[0]?.slug ?? '')
  }

  const openMegaMenu = (menuId: PropertyMegaMenuKind) => {
    clearPropertyMenuCloseTimer()
    if (openPropertyMenu !== menuId) resetMegaMenuSelection()
    setIsUserMenuOpen(false)
    setIsKprMenuOpen(false)
    setOpenPropertyMenu(menuId)
  }

  const openKprMenu = () => {
    clearPropertyMenuCloseTimer()
    setIsUserMenuOpen(false)
    setIsAiToolsOpen(false)
    setOpenPropertyMenu(null)
    setIsKprMenuOpen(true)
  }

  const closeMegaMenu = () => {
    clearPropertyMenuCloseTimer()
    setOpenPropertyMenu(null)
    setIsKprMenuOpen(false)
  }

  const openAiTools = () => {
    clearPropertyMenuCloseTimer()
    setOpenPropertyMenu(null)
    setIsKprMenuOpen(false)
    setIsUserMenuOpen(false)
    setIsMobileMenuOpen(false)
    setIsAiToolsOpen(true)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setIsUserMenuOpen(false)
    closeMegaMenu()
  }

  const scheduleMegaMenuClose = () => {
    clearPropertyMenuCloseTimer()
    propertyMenuCloseTimerRef.current = window.setTimeout(() => {
      setOpenPropertyMenu(null)
      setIsKprMenuOpen(false)
      propertyMenuCloseTimerRef.current = null
    }, 140)
  }

  const handlePropertySignOut = async () => {
    try {
      setIsUserMenuOpen(false)
      await signOut(firebaseAuth)
      showAppToast({ tone: 'success', title: 'Berhasil keluar', message: 'Akun properti sudah sign out.' })
      navigateTo('/')
    } catch (error) {
      console.error('Property sign out failed:', error)
      showAppToast({ tone: 'error', title: 'Sign out gagal', message: 'Coba lagi sebentar.' })
    }
  }

  return (
    <header
      className="property-header"
      onMouseEnter={clearPropertyMenuCloseTimer}
      onMouseLeave={scheduleMegaMenuClose}
    >
      <a className="property-brand" href="/" aria-label="Dihaw.co">
        <img className="property-brand-symbol" src="/icons/blue-192.png" width="36" height="36" alt="" />
        <span className="property-brand-wordmark">Dihaw<span className="property-brand-domain">.co</span></span>
      </a>

      <button
        className="property-mobile-menu-button"
        type="button"
        onClick={() => {
          closeMegaMenu()
          setIsMobileMenuOpen((current) => !current)
        }}
        aria-label={isMobileMenuOpen ? 'Tutup menu' : 'Buka menu'}
        aria-expanded={isMobileMenuOpen}
      >
        {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <nav className="property-nav" aria-label="Navigasi utama">
        {PROPERTY_MEGA_MENU_CONFIGS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={openPropertyMenu === item.id ? 'is-active' : ''}
            onMouseEnter={() => openMegaMenu(item.id)}
            onFocus={() => openMegaMenu(item.id)}
            onClick={() => {
              if (openPropertyMenu === item.id) {
                setOpenPropertyMenu(null)
                return
              }
              resetMegaMenuSelection()
              setOpenPropertyMenu(item.id)
            }}
            aria-expanded={openPropertyMenu === item.id}
            aria-controls="property-mega-menu"
          >
            {item.label}
            <ChevronDown size={15} />
          </button>
        ))}
        <button
          type="button"
          className={isKprMenuOpen ? 'is-active' : ''}
          onMouseEnter={openKprMenu}
          onFocus={openKprMenu}
          onClick={() => {
            setOpenPropertyMenu(null)
            setIsKprMenuOpen((current) => !current)
          }}
          aria-expanded={isKprMenuOpen}
          aria-controls="property-kpr-menu"
        >
          KPR
          <ChevronDown size={15} />
        </button>
        <button
          type="button"
          className={isAiToolsOpen ? 'is-active' : ''}
          onClick={openAiTools}
        >
          <WandSparkles size={16} />
          AI Tools
        </button>
      </nav>

      <div className="property-header-actions">
        {!isPropertyUserLoggedIn ? (
          <a
            className="property-action-link"
            href={agentRegisterHref}
            onClick={(event) => handlePropertyRegisterModalNavigation(event, 'agent')}
          >
            <LogIn size={16} />
            Login sebagai Agen
          </a>
        ) : null}
        {isPropertyUserLoggedIn ? (
          <div className="property-user-menu">
            <button
              className="property-user-menu-button"
              type="button"
              onClick={() => {
                setOpenPropertyMenu(null)
                setIsKprMenuOpen(false)
                setIsUserMenuOpen((current) => !current)
              }}
              aria-label="Buka menu profile"
              aria-expanded={isUserMenuOpen}
            >
              <UserRound size={18} />
              <span>Akun</span>
              <ChevronDown size={14} />
            </button>
            {isUserMenuOpen ? (
              <div className="property-user-menu-panel" role="menu">
                <a href={profileHref} role="menuitem" onClick={() => setIsUserMenuOpen(false)}>
                  <UserRound size={17} />
                  <strong>{profileLabel}</strong>
                </a>
                <button type="button" role="menuitem" onClick={() => void handlePropertySignOut()}>
                  <LogOut size={17} />
                  <strong>Sign out</strong>
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <a
            className="property-action-link is-primary"
            href={propertyRegisterHref}
            onClick={(event) => handlePropertyRegisterModalNavigation(event, 'user')}
          >
            <CirclePlus size={16} />
            Pasang Properti Gratis
            {isFreeListingMode ? <span className="property-free-badge">Free</span> : null}
          </a>
        )}
      </div>

      {activePropertyMenuConfig ? (
        <section
          id="property-mega-menu"
          className="property-mega-menu"
          aria-label={`Menu ${activePropertyMenuConfig.label}`}
          onMouseEnter={() => openMegaMenu(activePropertyMenuConfig.id)}
          onMouseLeave={scheduleMegaMenuClose}
        >
          <div className="property-mega-types" aria-label="Tipe properti">
            <h3>Tipe Properti</h3>
            <div>
              {PROPERTY_TYPE_MENU_ITEMS.map((type) => (
                <button
                  key={type.slug}
                  type="button"
                  className={selectedType?.slug === type.slug ? 'is-selected' : ''}
                  onMouseEnter={() => setSelectedTypeSlug(type.slug)}
                  onFocus={() => setSelectedTypeSlug(type.slug)}
                  onClick={() => setSelectedTypeSlug(type.slug)}
                >
                  <HomeIcon size={15} />
                  <span>{type.label}</span>
                  {type.isNew ? <em>NEW</em> : null}
                  <ChevronRight size={15} />
                </button>
              ))}
            </div>
          </div>

          <div className="property-mega-provinces" aria-label="Provinsi populer">
            <h3>Lokasi</h3>
            <div>
              {PROPERTY_LOCATION_MENU_ITEMS.map((location) => (
                <button
                  key={location.slug}
                  type="button"
                  className={selectedProvince?.slug === location.slug ? 'is-selected' : ''}
                  onMouseEnter={() => setSelectedProvinceSlug(location.slug)}
                  onFocus={() => setSelectedProvinceSlug(location.slug)}
                  onClick={() => setSelectedProvinceSlug(location.slug)}
                >
                  <span>{location.province}</span>
                  <ChevronRight size={15} />
                </button>
              ))}
            </div>
          </div>

          <div className="property-mega-cities" aria-label="Kota populer">
            <div className="property-mega-cities-head">
              <div>
                <span>{activePropertyMenuConfig.label}</span>
                <h3>
                  {selectedType?.label ?? 'Properti'} di {selectedProvince?.province ?? 'Indonesia'}
                </h3>
              </div>
              <span>{selectedProvince?.cities.length ?? 0} kota</span>
            </div>
            <a
              className="property-mega-all-cities"
              href={buildPropertyHref(
                `${activePropertyMenuConfig.hrefPrefix}/${selectedType?.slug ?? 'properti'}`,
                selectedProvince?.slug ?? 'indonesia',
              )}
              onClick={closeMegaMenu}
            >
              Semua kota di {selectedProvince?.province ?? 'Indonesia'}
              <ChevronRight size={16} />
            </a>
            <div className="property-mega-city-grid">
              {(selectedProvince?.cities ?? []).map((city) => (
                <a
                  key={`${selectedProvince?.slug}-${city}`}
                  href={buildPropertyHref(
                    `${activePropertyMenuConfig.hrefPrefix}/${selectedType?.slug ?? 'properti'}`,
                    propertySlug(city),
                  )}
                  onClick={closeMegaMenu}
                >
                  <Building2 size={15} />
                  {city}
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {isKprMenuOpen ? (
        <section
          id="property-kpr-menu"
          className="property-kpr-menu"
          aria-label="Menu KPR"
          onMouseEnter={openKprMenu}
          onMouseLeave={scheduleMegaMenuClose}
        >
          <div className="property-kpr-menu-inner">
            {KPR_MENU_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <a key={item.href} href={item.href} onClick={closeMegaMenu}>
                  <span>
                    <Icon size={22} />
                  </span>
                  <strong>{item.title}</strong>
                  <small>{item.description}</small>
                  <ChevronRight size={17} />
                </a>
              )
            })}
          </div>
        </section>
      ) : null}

      {isMobileMenuOpen ? (
        <div className="property-mobile-drawer-layer" onClick={closeMobileMenu}>
          <aside
            className="property-mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Menu Dihaw.co"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="property-mobile-drawer-head">
              <a className="property-brand" href="/" aria-label="Dihaw.co" onClick={closeMobileMenu}>
                <img className="property-brand-symbol" src="/icons/blue-192.png" width="36" height="36" alt="" />
                <span className="property-brand-wordmark">Dihaw<span className="property-brand-domain">.co</span></span>
              </a>
              <button type="button" onClick={closeMobileMenu} aria-label="Tutup menu">
                <X size={24} strokeWidth={2.6} />
              </button>
            </div>

            <nav className="property-mobile-main-menu" aria-label="Navigasi utama mobile">
              {PROPERTY_MEGA_MENU_CONFIGS.map((item) => (
                <a key={item.id} href={item.hrefPrefix} onClick={closeMobileMenu}>
                  <span>
                    {item.id === 'sale' ? <HomeIcon size={20} /> : item.id === 'rent' ? <Building2 size={20} /> : <Megaphone size={20} />}
                  </span>
                  <strong>{item.label}</strong>
                  <ChevronRight size={18} />
                </a>
              ))}
              <button type="button" onClick={openAiTools}>
                <span>
                  <WandSparkles size={20} />
                </span>
                <strong>AI Tools</strong>
                <ChevronRight size={18} />
              </button>
              {isAgentLoggedIn ? (
                <a href="/agent/profile" onClick={closeMobileMenu}>
                  <span>
                    <UserRound size={20} />
                  </span>
                  <strong>Profile Agent</strong>
                  <ChevronRight size={18} />
                </a>
              ) : isRegularPropertyUserLoggedIn ? (
                <a href="/pasang-property/profile" onClick={closeMobileMenu}>
                  <span>
                    <UserRound size={20} />
                  </span>
                  <strong>Profile</strong>
                  <ChevronRight size={18} />
                </a>
              ) : (
                <>
                  <a href="/agent" onClick={(event) => handlePropertyRegisterModalNavigation(event, 'agent', closeMobileMenu)}>
                    <span>
                      <LogIn size={20} />
                    </span>
                    <strong>Login sebagai Agen</strong>
                    <ChevronRight size={18} />
                  </a>
                  <a href="/pasang-property" onClick={(event) => handlePropertyRegisterModalNavigation(event, 'user', closeMobileMenu)}>
                    <span>
                      <CirclePlus size={20} />
                    </span>
                    <strong>Pasang Properti Gratis</strong>
                    <ChevronRight size={18} />
                  </a>
                </>
              )}
              {isPropertyUserLoggedIn ? (
                <button type="button" className="property-mobile-signout-button" onClick={() => void handlePropertySignOut()}>
                  <span>
                    <LogOut size={20} />
                  </span>
                  <strong>Sign out</strong>
                  <ChevronRight size={18} />
                </button>
              ) : null}
            </nav>
          </aside>
        </div>
      ) : null}
      <PropertyAiWidget open={isAiToolsOpen} onClose={() => setIsAiToolsOpen(false)} />
    </header>
  )
}

export function PropertySiteFooter() {
  return (
    <footer className="property-site-footer">
      <section className="property-footer-discovery" aria-label="Properti populer">
        <div className="property-footer-discovery-head">
          <div>
            <h2>Temukan properti dari kota dan provinsi populer.</h2>
          </div>
          <a className="property-footer-all-locations" href="/lokasi-properti">
            Direktori semua provinsi &amp; kota
            <ChevronRight size={15} aria-hidden="true" />
          </a>
        </div>

        <div className="property-footer-discovery-grid">
          <article className="property-footer-tab-panel property-footer-tab-panel-locations">
            <h3>Lokasi Properti Populer</h3>
            <p>
              Jelajahi properti di 12 provinsi pilihan beserta kota-kotanya. Lihat lokasi lainnya di direktori lengkap.
            </p>
            <div className="property-footer-all-location-grid">
              {FOOTER_LOCATION_LINKS.map((location) => (
                <section key={location.slug} className="property-footer-location-group" aria-labelledby={`footer-location-${location.slug}`}>
                  <h4 id={`footer-location-${location.slug}`}>{location.province}</h4>
                  <div className="property-footer-location-group-links">
                    <a href={buildPropertyHref('/jual', location.slug)}>
                      Properti dijual di {location.province}
                    </a>
                    <a href={buildPropertyHref('/sewa', location.slug)}>
                      Properti disewa di {location.province}
                    </a>
                    {location.cities
                      .filter((city) => propertySlug(city) !== location.slug)
                      .map((city) => (
                        <a key={city} href={buildPropertyHref('/jual', propertySlug(city))}>
                          Properti dijual di {city}
                        </a>
                      ))}
                  </div>
                </section>
              ))}
            </div>
          </article>
          <article className="property-footer-tab-panel property-footer-tab-panel-featured">
            <h3>Properti Unggulan</h3>
            <p>Jalur cepat ke pencarian properti bernilai tinggi, kota populer, dan tools pembiayaan.</p>
            <div className="property-footer-featured-grid">
              {FOOTER_FEATURED_PROPERTY_GROUPS.map((group) => (
                <nav key={group.title} className="property-footer-featured-card" aria-label={group.title}>
                  <strong>{group.title}</strong>
                  <span>{group.description}</span>
                  <div>
                    {group.links.map((item) => (
                      <a key={item.href} href={item.href} title={`${item.label} di Dihaw.co`}>
                        {item.label}
                        <ChevronRight size={14} aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                </nav>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="property-footer-main">
        <div className="property-footer-brand-panel">
          <a className="property-brand" href="/" aria-label="Dihaw.co">
            <img className="property-brand-symbol" src="/icons/blue-192.png" width="36" height="36" alt="" />
            <span className="property-brand-wordmark">Dihaw<span className="property-brand-domain">.co</span></span>
          </a>
          <div className="property-footer-socials" aria-label="Sosial media Dihaw.co">
            {FOOTER_SOCIAL_LINKS.map((item) => (
              <a key={item.label} href={item.href} aria-label={item.label} target="_blank" rel="noreferrer">
                <img src={item.iconSrc} alt="" loading="lazy" />
              </a>
            ))}
          </div>
          <p>
            Dihaw.co adalah platform listing properti untuk membantu pencari rumah,
            pemilik properti, agen, dan developer menampilkan informasi jual beli
            serta sewa properti dengan lebih rapi, mudah dicari, dan nyaman dibagikan.
          </p>
        </div>

        <div className="property-footer-link-groups">
          <nav aria-label="Perusahaan">
            <h3>Perusahaan</h3>
            {FOOTER_COMPANY_LINKS.map((item) => (
              <a key={item.href} href={item.href}>{item.label}</a>
            ))}
          </nav>
          <nav aria-label="Layanan">
            <h3>Layanan</h3>
            {FOOTER_SERVICE_LINKS.map((item) => (
              <a key={item.href} href={item.href}>{item.label}</a>
            ))}
          </nav>
          <nav aria-label="Dukungan">
            <h3>Dukungan</h3>
            {FOOTER_LEGAL_LINKS.map((item) => (
              <a key={item.href} href={item.href}>{item.label}</a>
            ))}
          </nav>
          <address>
            <h3>Hubungi Kami</h3>
            <a href="mailto:hello@dihaw.ai">hello@dihaw.ai</a>
            <a
              className="property-contact-whatsapp"
              href="https://wa.me/6285280871948?text=Halo%20Dihaw.co%2C%20saya%20mau%20tanya%20soal%20properti."
              target="_blank"
              rel="noreferrer"
            >
              <img src="/wa.webp" width="20" height="20" alt="" aria-hidden="true" />
              <span>+62 852 8087 1948</span>
            </a>
          </address>
        </div>
      </section>

      <section className="property-footer-ask-ai-section" aria-label="Ask AI about Dihaw.co">
        <div className="property-footer-ask-ai">
          <span>Ask AI about Dihaw.co</span>
          <div>
            {FOOTER_ASK_AI_LINKS.map((item) => (
              <a key={item.label} href={item.href} target="_blank" rel="noreferrer">
                <img src={item.iconSrc} alt="" loading="lazy" />
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="property-footer-bottom">
        <p>© Dihaw.co 2026. Platform utama untuk cari, jual, dan sewa properti di Indonesia.</p>
        <a href="#top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          Kembali ke atas
          <ArrowUp size={16} />
        </a>
      </section>
    </footer>
  )
}
